<script lang="ts">
	import { browser } from "$app/env";
	import { goto } from "$app/navigation";
	import { Button } from "@ishavskraft/shared";
	import { sessionValues, syncSessionValues } from "#lib/sessionValues.js";
	import type { Omraade } from "#lib/types/omraade.js";
	import { selectAreas } from "#lib/utils/areaUtils.js";
	import { areaFromLatLng } from "#lib/utils/areaFromLatLng.js";

	type Props = {
		/** Build the destination URL once the detected area is resolved.
		 *  Each surface (spotprisen / historikk) preserves its own view +
		 *  presentation + (historikk) year segments. */
		buildHref: (area: Omraade) => string;
		/** Extra classes merged onto the rendered <Button> — used by the
		 *  call site to control layout (e.g. `mr-auto` to anchor left). */
		class?: string;
		/** Bindable so the parent can render the error message wherever it
		 *  fits the surrounding layout (e.g. spanning the area-controls row
		 *  instead of squeezing under the button's grid column). */
		error?: string | null;
	};

	let { buildHref, class: classes = "", error = $bindable(null) }: Props = $props();

	let loading = $state(false);
	let denied = $state(false);

	// Once we've geo-detected, the affordance reads "Min posisjon (NOx)"
	// on every surface — not just when the user happens to be on the
	// matching area page. `detectedArea` is persisted in sessionStorage so
	// the variant survives page reloads + navigation.
	const detectedAreaCode = $derived($sessionValues.detectedArea ?? null);

	// Probe permission state so a previously-denied user sees the muted
	// placeholder up front. Listens for changes (omnibox / OS settings)
	// so the affordance returns if the user re-grants mid-session.
	$effect(() => {
		if (!browser || !navigator.permissions) return;
		let status: PermissionStatus | undefined;
		const apply = () => {
			denied = status?.state === "denied";
		};
		navigator.permissions
			.query({ name: "geolocation" as PermissionName })
			.then((s) => {
				status = s;
				apply();
				s.addEventListener("change", apply);
			})
			.catch(() => {
				// Older Safari / private mode can throw — leave the affordance
				// alone; the click will fire the OS prompt.
			});
		return () => {
			status?.removeEventListener("change", apply);
		};
	});

	function detect() {
		if (!browser || !navigator.geolocation) return;
		loading = true;
		error = null;
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const code = areaFromLatLng(pos.coords.latitude, pos.coords.longitude);
				const area = code ? selectAreas.find((a) => a.value === code) : undefined;
				if (!area) {
					loading = false;
					error = "Posisjon utenfor Norge";
					return;
				}
				syncSessionValues({ area: code as string, detectedArea: code as string });
				// Reset before navigating — when goto lands on the same
				// layout (e.g. /spotprisen → /spotprisen/nord-norge), this
				// component instance persists; without this the button
				// would stay stuck on "Finner posisjon…".
				loading = false;
				void goto(buildHref(area));
			},
			(err) => {
				loading = false;
				if (err.code === err.PERMISSION_DENIED) {
					// The permission-change listener catches this too, but set
					// directly in case the listener races.
					denied = true;
					return;
				}
				error = "Kunne ikke finne posisjon";
			},
			{ timeout: 8000, enableHighAccuracy: false }
		);
	}
</script>

{#if denied}
	<em class={["self-center text-sm text-[var(--fg-muted)]", classes]}>Posisjon avslått</em>
{:else}
	<Button link onclick={detect} disabled={loading} class={["", classes]}>
		{#if loading}
			Finner posisjon…
		{:else if detectedAreaCode}
			Min posisjon {detectedAreaCode}
		{:else}
			Bruk min posisjon
		{/if}
	</Button>
{/if}
